import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./i18n/I18nContext";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import AccountPage from "./pages/AccountPage";
import ProfileOnboarding from "./pages/ProfileOnboarding";
import PracticeSession from "./pages/PracticeSession";
import LessonTeach from "./pages/LessonTeach";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminPackages from "./pages/admin/AdminPackages";
import AdminBundles from "./pages/admin/AdminBundles";
import AdminLessons from "./pages/admin/AdminLessons";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminGrammarBlueprint from "./pages/admin/AdminGrammarBlueprint";

import "./App.css";

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  
  return children;
};

function I18nWrapper({ children }) {
  const { user } = useAuth();
  const lang = user?.native_language || localStorage.getItem("app_language") || "tr";
  return <I18nProvider language={lang}>{children}</I18nProvider>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      
      <Route path="/login" element={
        <PublicRoute><LoginPage /></PublicRoute>
      } />
      
      <Route path="/register" element={
        <PublicRoute><RegisterPage /></PublicRoute>
      } />
      
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      
      <Route path="/account" element={
        <ProtectedRoute><AccountPage /></ProtectedRoute>
      } />
      
      <Route path="/onboarding" element={
        <ProtectedRoute><ProfileOnboarding /></ProtectedRoute>
      } />
      
      <Route path="/practice/:sessionId" element={
        <ProtectedRoute><PracticeSession /></ProtectedRoute>
      } />
      <Route path="/lesson/:lessonId" element={
        <ProtectedRoute><LessonTeach /></ProtectedRoute>
      } />
      
      <Route path="/payment/success" element={
        <ProtectedRoute><PaymentSuccess /></ProtectedRoute>
      } />
      
      <Route path="/payment/cancel" element={
        <ProtectedRoute><PaymentCancel /></ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>
      } />
      
      <Route path="/admin/packages" element={
        <ProtectedRoute requireAdmin><AdminPackages /></ProtectedRoute>
      } />
      
      <Route path="/admin/bundles" element={
        <ProtectedRoute requireAdmin><AdminBundles /></ProtectedRoute>
      } />
      
      <Route path="/admin/lessons" element={
        <ProtectedRoute requireAdmin><AdminLessons /></ProtectedRoute>
      } />
      
      <Route path="/admin/users" element={
        <ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>
      } />
      
      <Route path="/admin/pricing" element={
        <ProtectedRoute requireAdmin><AdminPricing /></ProtectedRoute>
      } />
      
      <Route path="/admin/grammar-blueprint" element={
        <ProtectedRoute requireAdmin><AdminGrammarBlueprint /></ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <I18nWrapper>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </I18nWrapper>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
